import apiClient from "../../client/apiClient"

const BASE = "/student-status"

export type StudentStatusOption = {
  value: string
  label: string
  topLevel: "active" | "inactive"
  onRoll: boolean
  // Whether a student ON this status can have attendance marked at all —
  // struck-off is onRoll but not this (attendance stopped for them too).
  attendanceEligible: boolean
  // True only for "transfer_certificate" — picking it doesn't change status
  // directly, it opens the real Transfer Certificate issuance form instead,
  // so a TC always gets a serial number and a register entry.
  requiresIssuance: boolean
}

export type StatusHistoryEntry = {
  _id: string
  fromStatus: string
  toStatus: string
  reason: string
  effectiveDate: string
  source: string
  createdAt: string
  changedBy?: { firstName?: string; lastName?: string } | null
}

export type StudentStanding = {
  studentId: string
  grNumber: string
  name: string
  campusName: string
  className: string
  sectionName: string
  status: string
  statusLabel: string
  topLevel: "active" | "inactive"
  // Active + struck-off only — still on the rolls, still owes fees.
  onRoll: boolean
  // Active only. Suspension marks attendance, and struck-off's attendance
  // already stopped — use this to gate that, not onRoll.
  attendanceEligible: boolean
  // The two grounds a struck-off/left-without-information decision is
  // actually made on, and the number to call about it — shown up front so
  // the office isn't cross-checking Fees and Attendance for every name.
  feeSummary: { outstanding: number; unpaidMonths: number }
  lastAttendanceDate: string | null
  guardianContact: string
  history: StatusHistoryEntry[]
}

export const getStatusOptions = async (): Promise<{ data: StudentStatusOption[] }> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/options` })
  return res.data
}

export const getStudentStanding = async (
  studentId: string
): Promise<{ data: StudentStanding }> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/${studentId}` })
  return res.data
}

// Looked up the way the office actually has the child in front of them — by
// GR number — same pattern as the Transfer Certificate page's search.
export const getStudentStandingByGr = async (
  grNumber: string
): Promise<{ data: StudentStanding }> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/by-gr/${grNumber}` })
  return res.data
}

export type SectionBreakdownItem = {
  _id: string
  name: string
  count: number
}

export type RegisterStudent = {
  studentId: string
  grNumber: string
  name: string
  campusName: string
  className?: string
  sectionName?: string
  classId?: string | null
  sectionId?: string | null
  status: string
  statusLabel: string
  // Whichever happened more recently — a status change, or a leave/
  // suspension being recorded. `label` is "" for a status change (Status
  // already shows what it changed to; `reason` carries the why on its own)
  // and "Leave"/"Suspended" for the other, so the register reads as one
  // timeline instead of two separate, half-empty columns.
  lastChange: { date: string; label: string; reason: string } | null
}

// The register at the bottom of the status page — filterable to one status
// via the filter buttons, or "all" (the default) for every student, so the
// office can browse and pick one without already knowing the GR.
export const listStudentsByStatus = async (params: {
  page?: number
  limit?: number
  search?: string
  status?: string
  branchId?: string
  systemId?: string
  classId?: string
  sectionId?: string
  sessionId?: string
}): Promise<{
  data: RegisterStudent[]
  sectionBreakdown?: SectionBreakdownItem[]
  pagination: { total: number; page: number; limit: number; totalPages: number }
}> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/list/all`, params })
  return res.data
}

// A reason is required by the API for anything other than a return to Active —
// taking a child off the rolls must always have an answer to "why".
export const changeStudentStatus = async (
  studentId: string,
  data: { toStatus: string; reason?: string; effectiveDate?: string }
): Promise<{ success: boolean; changed: boolean; message: string }> => {
  const res = await apiClient({ method: "POST", url: `${BASE}/${studentId}`, data })
  return res.data
}

export type ResolvedStudent = {
  studentId: string
  grNumber: string
  name: string
  status: string
  statusLabel: string
  campusName: string
  // Only meaningful to the Suspension page — Student Status builds this same
  // shape from a register row (which doesn't carry it) for its own bulk list.
  attendanceEligible?: boolean
}

// Turns a pasted list of GR numbers (straight off an export) into real rows —
// the office sees who they actually are and what they're currently on before
// committing anything, same as picking them one at a time from the register.
export const resolveByGrNumbers = async (
  grNumbers: string[]
): Promise<{ success: boolean; found: ResolvedStudent[]; notFound: string[] }> => {
  const res = await apiClient({ method: "POST", url: `${BASE}/resolve-gr`, data: { grNumbers } })
  return res.data
}

// One status/reason/effective-date applied to however many students were
// selected — each still goes through the same admin-only-past-TC and
// reason-required rules a single change does, per student, so one failure
// doesn't sink the rest of the batch.
export const bulkChangeStatus = async (data: {
  studentIds: string[]
  toStatus: string
  reason?: string
  effectiveDate?: string
}): Promise<{
  success: boolean
  changed: number
  unchanged: number
  failed: { studentId: string; message: string }[]
  message: string
}> => {
  const res = await apiClient({ method: "POST", url: `${BASE}/bulk`, data })
  return res.data
}

// Called from the admission screen so the office sees, as they type the GR,
// that this child has been here before and how they left.
export const checkPriorStanding = async (
  grNumber: string
): Promise<{
  found: boolean
  data?: {
    studentId: string
    grNumber: string
    name: string
    status: string
    statusLabel: string
    previouslyLeft: boolean
    reAdmissionFeeApplies: boolean
    lastDeparture: { statusLabel: string; reason: string; date: string } | null
  }
}> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/prior-standing/${grNumber}` })
  return res.data
}

export const reAdmitStudent = async (
  studentId: string,
  data: { reAdmissionFeeCollected: boolean; reason?: string }
) => {
  const res = await apiClient({ method: "POST", url: `${BASE}/${studentId}/re-admit`, data })
  return res.data
}

// Preview (GET) or commit (POST with apply) the four-months-unpaid sweep.
export const autoStruckOffPreview = async (params: {
  branchId?: string
  sessionId?: string
  months?: number
}) => {
  const res = await apiClient({
    method: "GET",
    url: `${BASE}/auto/struck-off`,
    params,
  })
  return res.data
}

export const autoStruckOffApply = async (data: {
  sessionId?: string
  months?: number
}) => {
  const res = await apiClient({
    method: "POST",
    url: `${BASE}/auto/struck-off`,
    data: { ...data, apply: true },
  })
  return res.data
}
